import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { SortItem } from '@/types/table.ts'
import type { FilterDefinition } from '@/constants/filters'
import { cloneDeep } from 'lodash'
import { useQueryClient } from '@tanstack/vue-query'
import { type UseTableOptions } from 'composables/useTable.ts'
import storeConfigs from 'constants/storeConfigs.ts'

// Define the change type enum for consumers
export enum TableChangeType {
	PAGINATION = 'pagination',
	SORT = 'sort',
	SEARCH = 'search',
	FILTER = 'filter',
	SELECTION = 'selection',
	EXPANSION = 'expansion',
	RESET = 'reset',
	FULL_UPDATE = 'full_update',
}

export interface TableState {
	endpoint?: string
	page: number
	itemsPerPage: number | undefined
	search: string | undefined
	sortBy: SortItem[]
	expandedItems: any[]
	selectedItems: any[]
	filters: Filter[]
}

export type Filter = {
	value: any
	definition: FilterDefinition
}

export const DEFAULT_TABLE_STATE: TableState = {
	page: 1,
	itemsPerPage: undefined,
	search: '',
	sortBy: [{ key: 'id', order: 'desc' }],
	expandedItems: [],
	selectedItems: [],
	filters: [],
}

export const useTableStateStore = defineStore(
	storeConfigs.tableState.name,
	() => {
		const queryClient = useQueryClient()
		const tables = ref<Record<string, TableState>>({})
		const lastChangeInfo = ref<{
			tableId: string
			type: TableChangeType
			timestamp: number
		} | null>(null)

		/**
		 * Get a specific table's state by its ID
		 * Returns a default state if the table state doesn't exist
		 */
		const getTableState = computed(() => {
			return (tableId: string): TableState =>
				tables.value[tableId] ?? { ...cloneDeep(DEFAULT_TABLE_STATE) }
		})

		const tableStateExists = computed(() => {
			return (tableId: string): boolean => !!tables.value[tableId]
		})

		/**
		 * Get count of tables with saved state
		 */
		const tableCount = computed(() => {
			return Object.keys(tables.value).length
		})

		// Actions

		/**
		 * Track change for subscription consumers
		 * @param tableId Table ID that changed
		 * @param type Type of change
		 */
		function trackChange(tableId: string, type: TableChangeType) {
			lastChangeInfo.value = {
				tableId,
				type,
				timestamp: Date.now(),
			}
		}

		/**
		 * Save or update a table's state
		 * @param tableId Unique identifier for the table
		 * @param state Table state to save
		 */
		function saveTableState(tableId: string, state: Partial<TableState>) {
			if (!tables.value[tableId]) {
				tables.value[tableId] = { ...cloneDeep(DEFAULT_TABLE_STATE) }
			}

			tables.value[tableId] = {
				...tables.value[tableId],
				...state,
			}

			trackChange(tableId, TableChangeType.FULL_UPDATE)
		}

		/**
		 * Get or create table state
		 * @param tableId Unique identifier for the table
		 * @param defaultState Optional default state if creating new
		 * @param init
		 */
		function getOrCreateTableState(
			tableId: string,
			defaultState?: Partial<TableState>,
			init?: boolean
		): TableState {
			if (!tables.value[tableId] || init) {
				tables.value[tableId] = {
					...cloneDeep(DEFAULT_TABLE_STATE),
					...(defaultState || {}),
				}
				trackChange(tableId, TableChangeType.FULL_UPDATE)
			}
			return tables.value[tableId]
		}

		/**
		 * Reset a table's state to default values
		 * @param tableId Unique identifier for the table
		 */
		function resetTableState(tableId: string) {
			if (tables.value[tableId]) {
				tables.value[tableId] = { ...cloneDeep(DEFAULT_TABLE_STATE) }
				trackChange(tableId, TableChangeType.RESET)
			}
		}

		/**
		 * Reset all tables' state
		 */
		function resetAllTables() {
			tables.value = {}
			// We're resetting all tables, so there's no specific tableId
			trackChange('all', TableChangeType.RESET)
		}

		/**
		 * Update pagination for a specific table
		 */
		function updatePagination(
			tableId: string,
			page: number,
			itemsPerPage?: number
		) {
			const state = getOrCreateTableState(tableId)
			state.page = page
			if (itemsPerPage !== undefined) {
				state.itemsPerPage = itemsPerPage
			}
			trackChange(tableId, TableChangeType.PAGINATION)
		}

		/**
		 * Update sorting for a specific table
		 */
		function updateSorting(tableId: string, sortBy: SortItem[]) {
			const state = getOrCreateTableState(tableId)
			state.sortBy = sortBy
			trackChange(tableId, TableChangeType.SORT)
		}

		/**
		 * Update search term for a specific table
		 */
		function updateSearch(tableId: string, search: string) {
			const state = getOrCreateTableState(tableId)
			state.search = search
			// Reset to first page when search changes
			state.page = 1
			trackChange(tableId, TableChangeType.SEARCH)
		}

		/**
		 * Update filters for a specific table
		 */
		function updateFilters(tableId: string, filters: Filter[]) {
			const state = getOrCreateTableState(tableId)
			state.filters = filters
			// Reset to first page when filters change
			state.page = 1
			trackChange(tableId, TableChangeType.FILTER)
		}

		function addOrUpdateFilter(
			tableId: string,
			filter?: Filter,
			value = undefined
		) {
			if (!filter) return
			const state = getOrCreateTableState(tableId)
			const filterIndex = getFilterIndex(state, filter.definition.component)
			value = value ?? filter.value

			if (!value) {
				setFilterValue(tableId, filter.definition)
				return
			}

			if (filterIndex !== -1) {
				state.filters[filterIndex].value = value
			} else {
				state.filters.push(filter)
			}

			trackChange(tableId, TableChangeType.FILTER)
		}

		function getFilterValue(tableId: string, filter: FilterDefinition) {
			const state = getOrCreateTableState(tableId)
			return state.filters[getFilterIndex(state, filter.component)]?.value
		}

		function getFiltersValueFormatted(tableId: string) {
			const data: {
				q?: string[]
				v?: string[]
				group: string
				tsgt?: number
				tslt?: number
			} = { group: '' }

			getFilters(tableId).forEach((filter) => {
				let value
				const { definition } = filter

				if (
					filter.definition.type === 'date' &&
					typeof filter.value !== 'undefined'
				) {
					const startDate = new Date(filter.value)
					const endDate = new Date(filter.value)
					startDate.setHours(0, 0, 0, 0)
					endDate.setHours(23, 59, 59, 0)
					data.tsgt = Math.floor(startDate.getTime() / 1000)
					data.tslt = Math.floor(endDate.getTime() / 1000)
				} else if (
					definition.type === 'date-from' &&
					typeof filter.value !== 'undefined'
				) {
					const startDate = new Date(filter.value)
					startDate.setHours(0, 0, 0, 0)
					data.tsgt = Math.floor(startDate.getTime() / 1000)
				} else if (
					definition.type === 'date-to' &&
					typeof filter.value !== 'undefined'
				) {
					const endDate = new Date(filter.value)
					endDate.setHours(23, 59, 59, 0)
					data.tslt = Math.floor(endDate.getTime() / 1000)
				} else if (typeof filter.value === 'object') {
					value = filter.value[definition.value.field]
				} else {
					value = filter.value
				}
				if (value && definition) {
					data.q = data.q || []
					data.v = data.v || []
					data.q.push(definition.field)
					data.v.push(value)
					if (definition.value.group) {
						data.group = definition.value.group
					}
				}
			})

			if (
				Object.keys(data).length === 0 ||
				(Object.keys(data).length === 1 && data.group === '')
			) {
				return undefined
			}
			return data
		}

		function getFilters(tableId: string, active: boolean = true) {
			const state = getTableState.value(tableId)
			const filters = state.filters
			if (!active) return filters
			return filters.filter((filter) => typeof filter.value !== 'undefined')
		}

		function getFilterIndex(state: TableState, component: string) {
			return state.filters.findIndex(
				(item) => item.definition.component === component
			)
		}

		function setFilterValue(
			tableId: string,
			filter: FilterDefinition,
			value: any | undefined = undefined
		) {
			const state = getOrCreateTableState(tableId)
			const filterIndex = getFilterIndex(state, filter.component)
			if (filterIndex !== -1) {
				state.filters[filterIndex].value = value
				trackChange(tableId, TableChangeType.FILTER)
			}
		}

		/**
		 * Update selected items for a specific table
		 */
		function updateSelectedItems(tableId: string, selectedItems: any[]) {
			const state = getOrCreateTableState(tableId)
			state.selectedItems = selectedItems
			trackChange(tableId, TableChangeType.SELECTION)
		}

		/**
		 * Update expanded items for a specific table
		 */
		function updateExpandedItems(tableId: string, expandedItems: any[]) {
			const state = getOrCreateTableState(tableId)
			state.expandedItems = expandedItems
			trackChange(tableId, TableChangeType.EXPANSION)
		}

		/**
		 * Invalidate TanStack Query cache for a specific table
		 * @param tableId Unique identifier for the table
		 * @param endpoint API endpoint associated with the table
		 */
		function invalidateTableCache(tableId: string, endpoint?: string) {
			if (endpoint) {
				// Invalidate by endpoint
				queryClient.invalidateQueries({
					queryKey: ['collection', endpoint],
					exact: false,
				})
			} else {
				// Try to extract endpoint from tableId
				const parts = tableId.split('-')
				if (parts.length > 1) {
					const possibleEndpoint = parts[parts.length - 1]
					queryClient.invalidateQueries({
						queryKey: ['collection', possibleEndpoint],
						exact: false,
					})
				}
			}
		}

		// Return public API
		return {
			// State
			tables,
			lastChangeInfo,

			// Getters
			getTableState,
			tableCount,
			tableStateExists,

			// Actions
			addOrUpdateFilter,
			getFilterValue,
			getFiltersValueFormatted,
			setFilterValue,
			saveTableState,
			getFilters,
			getOrCreateTableState,
			resetTableState,
			resetAllTables,
			updatePagination,
			updateSorting,
			updateSearch,
			updateFilters,
			updateSelectedItems,
			updateExpandedItems,
			invalidateTableCache,
		}
	}
)
